/**
 * Screen-reader summary of the latest solve result.
 *
 * The map paints new routes silently: sighted users see them appear, but
 * assistive tech hears nothing on paths the completion dialog doesn't cover.
 * This derives a one-line summary ("Optimization complete: 3 routes, 41 stops
 * served, 2 trips dropped") that LiveAnnouncer renders into a polite live
 * region. The numbers mirror what the Vehicle Routes panel and drops tables
 * (the map's accessible twins) show in full.
 */
import { createSelector } from '@reduxjs/toolkit';
import { IMMOVABLE_STOP_TYPES } from 'store/functions/resultEditing/evaluator';

/**
 * Pure: build the announcement string, or null when there are no results.
 *
 * @param {Array|null|undefined} routeStops - data.route_stops rows.
 * @param {Array|null|undefined} droppedTrips - data.dropped_trips rows.
 * @param {Array|null|undefined} droppedLocations - data.dropped_locations rows.
 * @returns {string|null}
 */
export function buildSolveAnnouncement(routeStops, droppedTrips, droppedLocations) {
  if (!Array.isArray(routeStops) || routeStops.length === 0) return null;

  const vehicles = new Set();
  let servedStops = 0;
  for (const row of routeStops) {
    if (!row || typeof row !== 'object') continue;
    if (row.vehicleId != null) vehicles.add(row.vehicleId);
    // Depot start/end and restock rows aren't customer stops.
    if (!IMMOVABLE_STOP_TYPES.has(row.stopType)) servedStops += 1;
  }
  if (vehicles.size === 0 && servedStops === 0) return null;

  const routeCount = vehicles.size || 1;
  const parts = [
    `Optimization complete: ${routeCount} ${routeCount === 1 ? 'route' : 'routes'}`,
    `${servedStops} ${servedStops === 1 ? 'stop' : 'stops'} served`,
  ];
  const droppedTripCount = Array.isArray(droppedTrips) ? droppedTrips.length : 0;
  const droppedLocationCount = Array.isArray(droppedLocations) ? droppedLocations.length : 0;
  if (droppedTripCount > 0) {
    parts.push(`${droppedTripCount} ${droppedTripCount === 1 ? 'trip' : 'trips'} dropped`);
  }
  if (droppedLocationCount > 0) {
    parts.push(`${droppedLocationCount} ${droppedLocationCount === 1 ? 'location' : 'locations'} dropped`);
  }
  parts.push('Details are in the Vehicle Routes panel and results tables.');
  return parts.join('. ') + '.';
}

/**
 * Memoized so the returned string is reference-stable across unrelated store
 * updates (e.g. solve-poller ticks) — the live region only re-announces when
 * the results actually change.
 */
export const selectSolveAnnouncement = createSelector(
  [
    (state) => state?.inputData?.present?.data?.route_stops,
    (state) => state?.inputData?.present?.data?.dropped_trips,
    (state) => state?.inputData?.present?.data?.dropped_locations,
  ],
  buildSolveAnnouncement
);
